/**
 * Sound Effects Manager for Par Seeker
 * Provides button clicks, golf swing sounds, and other UI feedback
 */

class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.3;
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Whip crack sound - sharp snap for button clicks
  playSwish() {
    if (!this.enabled) return;
    this.init();
    
    // Whip crack: sharp transient + quick noise burst
    const duration = 0.12;
    const bufferSize = this.audioContext.sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Generate whip crack: extremely fast attack, sharp decay
    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      // Sharp spike at the beginning (the "crack")
      const crack = t < 0.05 ? Math.pow(1 - (t / 0.05), 8) : 0;
      // Quick tail noise
      const tail = Math.pow(1 - t, 6) * 0.3;
      const envelope = crack + tail;
      // Mix of noise for texture
      data[i] = (Math.random() * 2 - 1) * envelope;
    }
    
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    
    // High-pass filter for sharp crack character
    const highpass = this.audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    highpass.Q.setValueAtTime(0.7, this.audioContext.currentTime);
    
    // Slight bandpass for "snap" emphasis
    const bandpass = this.audioContext.createBiquadFilter();
    bandpass.type = 'peaking';
    bandpass.frequency.setValueAtTime(4000, this.audioContext.currentTime);
    bandpass.gain.setValueAtTime(6, this.audioContext.currentTime);
    bandpass.Q.setValueAtTime(2, this.audioContext.currentTime);
    
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(this.volume * 0.7, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);
    
    source.connect(highpass);
    highpass.connect(bandpass);
    bandpass.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    source.start();
    
    // Add a sharp "pop" transient for extra crack
    const pop = this.audioContext.createOscillator();
    const popGain = this.audioContext.createGain();
    pop.connect(popGain);
    popGain.connect(this.audioContext.destination);
    pop.frequency.setValueAtTime(3500, this.audioContext.currentTime);
    pop.frequency.exponentialRampToValueAtTime(800, this.audioContext.currentTime + 0.02);
    popGain.gain.setValueAtTime(this.volume * 0.4, this.audioContext.currentTime);
    popGain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.03);
    pop.start();
    pop.stop(this.audioContext.currentTime + 0.03);
  }

  // Button click - now uses swish sound
  playClick() {
    this.playSwish();
  }

  // Success ping - pleasant confirmation sound
  playSuccess() {
    if (!this.enabled) return;
    this.init();
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.setValueAtTime(523.25, this.audioContext.currentTime); // C5
    oscillator.frequency.setValueAtTime(659.25, this.audioContext.currentTime + 0.1); // E5
    oscillator.frequency.setValueAtTime(783.99, this.audioContext.currentTime + 0.2); // G5
    
    gainNode.gain.setValueAtTime(this.volume * 0.4, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.4);
  }

  // Golf swing whoosh sound
  playGolfSwing() {
    if (!this.enabled) return;
    this.init();
    
    // Create white noise for whoosh
    const bufferSize = this.audioContext.sampleRate * 0.4; // 400ms
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Generate filtered noise that sounds like a swing
    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      // Envelope: quick attack, sustain, quick decay
      const envelope = Math.sin(t * Math.PI) * Math.pow(1 - t, 0.5);
      // Noise with some filtering effect
      data[i] = (Math.random() * 2 - 1) * envelope;
    }
    
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    
    // Low-pass filter to make it sound like a whoosh
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    filter.frequency.exponentialRampToValueAtTime(400, this.audioContext.currentTime + 0.3);
    
    // High-pass to remove rumble
    const highpass = this.audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(200, this.audioContext.currentTime);
    
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(this.volume * 0.6, this.audioContext.currentTime);
    
    source.connect(filter);
    filter.connect(highpass);
    highpass.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    source.start();
  }

  // Golf ball hit - crisp impact sound
  playBallHit() {
    if (!this.enabled) return;
    this.init();
    
    // Impact sound: quick sine burst + noise
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Quick pitch drop for impact feel
    oscillator.frequency.setValueAtTime(1200, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(150, this.audioContext.currentTime + 0.08);
    
    gainNode.gain.setValueAtTime(this.volume * 0.5, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.1);
    
    // Add click component
    setTimeout(() => {
      const click = this.audioContext.createOscillator();
      const clickGain = this.audioContext.createGain();
      click.connect(clickGain);
      clickGain.connect(this.audioContext.destination);
      click.frequency.setValueAtTime(3000, this.audioContext.currentTime);
      clickGain.gain.setValueAtTime(this.volume * 0.2, this.audioContext.currentTime);
      clickGain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.02);
      click.start();
      click.stop(this.audioContext.currentTime + 0.02);
    }, 5);
  }

  // Score entry confirmation - quick positive beep
  playScoreEntry() {
    if (!this.enabled) return;
    this.init();
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, this.audioContext.currentTime); // A5
    
    gainNode.gain.setValueAtTime(this.volume * 0.25, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.08);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.08);
  }

  // Birdie celebration sound!
  playBirdie() {
    if (!this.enabled) return;
    this.init();
    
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, this.audioContext.currentTime);
        gain.gain.setValueAtTime(this.volume * 0.3, this.audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
        osc.start();
        osc.stop(this.audioContext.currentTime + 0.15);
      }, i * 100);
    });
  }

  // Error/warning sound
  playError() {
    if (!this.enabled) return;
    this.init();
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
    
    gainNode.gain.setValueAtTime(this.volume * 0.2, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.15);
  }
}

// Create singleton instance
const soundEffects = new SoundEffects();

export default soundEffects;
